import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ethers } from 'ethers';
import { useAccount, useWriteContract } from 'wagmi';
import { CPGABI } from '../../../ABI/ABI';
import Loader from '../../../components/loader/Loader';
import {
    CPGAddress,
    initialTokenList,
    targetChainId,
} from '../../../constant/constant';
import {
    TRANSACTION_ERROR_MSG,
    USER_REJECT_TRANSACTION_MSG,
} from '../../../constant/toasterMessage';
import {
    fireErrorToast,
    fireSuccessToast,
    shortenWalletAddress,
} from '../../../utils/commonFunction';
import {
    FEE_PAYMENT_TYPES,
    MAX_FEE_BPS,
    bpsToPercentLabel,
    fetchGatewayAdminSettings,
    hoursInputToSeconds,
    isWalletContractOwner,
    percentInputToBps,
    secondsToHoursLabel,
} from '../../../utils/adminContractSettings';
import { waitForSuccessfulTransactionReceipt } from '../../../utils/transactionReceipt';

const SectionCard = ({ title, description, children, className = '' }) => (
    <section
        className={`overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-950/50 ${className}`}
    >
        <div className="border-b border-zinc-200 px-5 py-4 dark:border-zinc-800">
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">
                {title}
            </h2>
            {description && (
                <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
                    {description}
                </p>
            )}
        </div>
        <div className="space-y-4 px-5 py-4">{children}</div>
    </section>
);

const FieldLabel = ({ children }) => (
    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
        {children}
    </label>
);

const TextInput = ({ className = '', ...props }) => (
    <input
        className={`cpg-input w-full ${className}`}
        {...props}
    />
);

const ActionButton = ({
    children,
    onClick,
    disabled,
    variant = 'primary',
    className = '',
}) => {
    const variants = {
        primary:
            'bg-teal-700 text-white hover:bg-teal-600 dark:bg-teal-600 dark:hover:bg-teal-500',
        secondary:
            'border border-zinc-200 bg-white text-zinc-800 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:hover:bg-zinc-800',
        danger:
            'border border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100 dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-200 dark:hover:bg-rose-950/60',
    };
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${variants[variant] || variants.primary} ${className}`}
        >
            {children}
        </button>
    );
};

const TimelockBanner = ({
    label,
    pendingAddress,
    effectiveAt,
    onExecute,
    onCancel,
    busy,
}) => {
    if (!pendingAddress) return null;
    const ready =
        effectiveAt > 0 && Math.floor(Date.now() / 1000) >= effectiveAt;
    return (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/35 dark:text-amber-100">
            <p className="font-medium">Pending {label}</p>
            <p className="mt-1 font-mono text-xs">
                {shortenWalletAddress(pendingAddress)}
            </p>
            <p className="mt-1 text-xs">
                {ready
                    ? 'Timelock expired — you can execute now.'
                    : effectiveAt
                      ? `Effective ${new Date(effectiveAt * 1000).toLocaleString()}`
                      : 'Scheduled — waiting for timelock.'}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
                <ActionButton
                    variant="primary"
                    disabled={busy || !ready}
                    onClick={onExecute}
                >
                    Execute change
                </ActionButton>
                <ActionButton
                    variant="danger"
                    disabled={busy}
                    onClick={onCancel}
                >
                    Cancel
                </ActionButton>
            </div>
        </div>
    );
};

const AdminContractControls = () => {
    const { address } = useAccount();
    const { writeContractAsync } = useWriteContract();
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [txBusy, setTxBusy] = useState(false);
    const [error, setError] = useState('');

    const [feeRecipient, setFeeRecipient] = useState('');
    const [feePercents, setFeePercents] = useState({
        directPaymentBps: '0',
        lockedEscrowBps: '0',
        lockedClaimBps: '0',
        disputeResolveBps: '0',
    });
    const [disputePeriodHours, setDisputePeriodHours] = useState('');
    const [minDisputePeriodHours, setMinDisputePeriodHours] = useState('');
    const [maxPaymentAmount, setMaxPaymentAmount] = useState('');
    const [timelockDelayHours, setTimelockDelayHours] = useState('');
    const [newDisputeAdmin, setNewDisputeAdmin] = useState('');
    const [newTokenAddress, setNewTokenAddress] = useState('');
    const [removeTokenAddress, setRemoveTokenAddress] = useState('');

    const isOwner = useMemo(
        () => isWalletContractOwner(address, settings?.owner),
        [address, settings?.owner],
    );

    const applySettingsToForm = useCallback((data) => {
        if (!data) return;
        setFeeRecipient(data.fees?.recipient || '');
        setFeePercents({
            directPaymentBps: String((data.fees?.directPaymentBps || 0) / 100),
            lockedEscrowBps: String((data.fees?.lockedEscrowBps || 0) / 100),
            lockedClaimBps: String((data.fees?.lockedClaimBps || 0) / 100),
            disputeResolveBps: String(
                (data.fees?.disputeResolveBps || 0) / 100,
            ),
        });
        setDisputePeriodHours(
            data.disputeResolutionPeriodSeconds
                ? String(data.disputeResolutionPeriodSeconds / 3600)
                : '',
        );
        setMinDisputePeriodHours(
            data.minDisputeResolutionPeriodSeconds
                ? String(data.minDisputeResolutionPeriodSeconds / 3600)
                : '',
        );
        setMaxPaymentAmount(data.maxPaymentAmount || '0');
        setTimelockDelayHours(
            data.adminTimelockDelaySeconds
                ? String(data.adminTimelockDelaySeconds / 3600)
                : '',
        );
        setNewDisputeAdmin(data.disputeAdmin || '');
        setRemoveTokenAddress(data.whitelistedTokens?.[0]?.address || '');
    }, []);

    const loadSettings = useCallback(async () => {
        if (!address || !CPGAddress) return;
        setLoading(true);
        setError('');
        try {
            const data = await fetchGatewayAdminSettings({
                gatewayAddress: CPGAddress,
                gatewayAbi: CPGABI,
                tokenList: initialTokenList,
            });
            setSettings(data);
            applySettingsToForm(data);
        } catch (err) {
            console.error(err);
            setError(err?.message || 'Failed to load contract settings');
        } finally {
            setLoading(false);
        }
    }, [address, applySettingsToForm]);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const runTx = async (functionName, args = []) => {
        if (!CPGAddress) return;
        setTxBusy(true);
        try {
            const hash = await writeContractAsync({
                address: CPGAddress,
                abi: CPGABI,
                functionName,
                args,
                chainId: targetChainId,
            });
            await waitForSuccessfulTransactionReceipt(hash);
            fireSuccessToast('On-chain update confirmed');
            await loadSettings();
        } catch (err) {
            const msg = String(
                err?.shortMessage || err?.message || TRANSACTION_ERROR_MSG,
            );
            if (msg.toLowerCase().includes('user rejected')) {
                fireErrorToast(USER_REJECT_TRANSACTION_MSG);
            } else {
                fireErrorToast(msg);
            }
        } finally {
            setTxBusy(false);
        }
    };

    const handleSaveAllFees = async () => {
        const directPaymentBps = percentInputToBps(feePercents.directPaymentBps);
        const lockedEscrowBps = percentInputToBps(feePercents.lockedEscrowBps);
        const lockedClaimBps = percentInputToBps(feePercents.lockedClaimBps);
        const disputeResolveBps = percentInputToBps(
            feePercents.disputeResolveBps,
        );
        if (
            directPaymentBps === null ||
            lockedEscrowBps === null ||
            lockedClaimBps === null ||
            disputeResolveBps === null
        ) {
            fireErrorToast(`Each fee must be between 0% and ${MAX_FEE_BPS / 100}%`);
            return;
        }
        let recipient = feeRecipient.trim();
        try {
            recipient = recipient ? ethers.getAddress(recipient) : ethers.ZeroAddress;
        } catch (_error) {
            fireErrorToast('Enter a valid fee recipient address');
            return;
        }
        await runTx('setPaymentFees', [
            recipient,
            directPaymentBps,
            lockedEscrowBps,
            lockedClaimBps,
            disputeResolveBps,
        ]);
    };

    const handleSaveSingleFee = async (feeType, percentKey) => {
        const bps = percentInputToBps(feePercents[percentKey]);
        if (bps === null) {
            fireErrorToast(`Fee must be between 0% and ${MAX_FEE_BPS / 100}%`);
            return;
        }
        await runTx('setPaymentFeeBps', [feeType, bps]);
    };

    const handleScheduleFeeRecipient = async () => {
        try {
            const recipient = ethers.getAddress(feeRecipient.trim());
            await runTx('scheduleSetFeeRecipient', [recipient]);
        } catch (_error) {
            fireErrorToast('Enter a valid fee recipient address');
        }
    };

    const handleSaveDisputePeriods = async () => {
        const minSeconds = hoursInputToSeconds(minDisputePeriodHours);
        const periodSeconds = hoursInputToSeconds(disputePeriodHours);
        if (minSeconds === null || periodSeconds === null) {
            fireErrorToast('Enter valid hour values for dispute periods');
            return;
        }
        if (periodSeconds < minSeconds) {
            fireErrorToast('Dispute window must be at least the minimum period');
            return;
        }
        await runTx('setMinDisputeResolutionPeriod', [BigInt(minSeconds)]);
        await runTx('setDisputeResolutionPeriod', [BigInt(periodSeconds)]);
    };

    const handleSaveMaxPayment = async () => {
        const raw = String(maxPaymentAmount || '').trim();
        if (!raw || !/^\d+$/.test(raw)) {
            fireErrorToast('Enter max payment amount as a whole number (token units)');
            return;
        }
        await runTx('setMaxPaymentAmount', [BigInt(raw)]);
    };

    const handleSaveTimelockDelay = async () => {
        const seconds = hoursInputToSeconds(timelockDelayHours);
        if (seconds === null) {
            fireErrorToast('Enter a valid timelock delay in hours');
            return;
        }
        await runTx('setAdminTimelockDelay', [BigInt(seconds)]);
    };

    const handleScheduleDisputeAdmin = async () => {
        try {
            const admin = ethers.getAddress(newDisputeAdmin.trim());
            await runTx('scheduleSetDisputeAdmin', [admin]);
        } catch (_error) {
            fireErrorToast('Enter a valid dispute admin address');
        }
    };

    const handleWhitelistToken = async () => {
        try {
            const token = ethers.getAddress(newTokenAddress.trim());
            await runTx('whitelistToken', [token]);
            setNewTokenAddress('');
        } catch (_error) {
            fireErrorToast('Enter a valid token contract address');
        }
    };

    const handleRemoveToken = async () => {
        try {
            const token = ethers.getAddress(removeTokenAddress.trim());
            await runTx('removeTokenFromWhitelist', [token]);
        } catch (_error) {
            fireErrorToast('Select or enter a valid token address');
        }
    };

    if (loading && !settings) {
        return (
            <div className="mt-8 flex justify-center rounded-2xl border border-zinc-200 bg-white py-16 dark:border-zinc-800 dark:bg-zinc-950/50">
                <Loader size="28px" />
            </div>
        );
    }

    if (!isOwner) {
        return (
            <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/35 dark:text-amber-100">
                Contract owner actions require the diamond owner wallet (
                {settings?.owner
                    ? shortenWalletAddress(settings.owner)
                    : 'not loaded'}
                ). Dispute admin can still moderate disputes but cannot change fees
                or platform settings.
            </div>
        );
    }

    return (
        <div className="mt-10 space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h2 className="text-xl font-semibold text-zinc-900 dark:text-white">
                        Contract administration
                    </h2>
                    <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
                        Owner-only gateway settings on{' '}
                        {shortenWalletAddress(CPGAddress)}. Fee recipient and
                        dispute admin changes use the on-chain timelock.
                    </p>
                </div>
                <ActionButton
                    variant="secondary"
                    disabled={loading || txBusy}
                    onClick={loadSettings}
                >
                    <i
                        className={`fa-solid fa-rotate-right text-xs ${loading ? 'animate-spin' : ''}`}
                    />
                    Refresh settings
                </ActionButton>
            </div>

            {error && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900 dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-200">
                    {error}
                </div>
            )}

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <SectionCard
                    title="Platform fees"
                    description={`Max ${MAX_FEE_BPS / 100}% per fee type · basis points on-chain`}
                >
                    <div>
                        <FieldLabel>Fee recipient</FieldLabel>
                        <TextInput
                            value={feeRecipient}
                            onChange={(e) => setFeeRecipient(e.target.value)}
                            placeholder="0x…"
                        />
                        <p className="mt-1 text-xs text-zinc-500">
                            Current:{' '}
                            {settings?.fees?.recipient
                                ? shortenWalletAddress(settings.fees.recipient)
                                : 'none'}
                        </p>
                    </div>

                    <TimelockBanner
                        label="fee recipient"
                        pendingAddress={settings?.pendingFeeRecipient}
                        effectiveAt={settings?.feeRecipientEffectiveAt}
                        busy={txBusy}
                        onExecute={() => runTx('executeSetFeeRecipient')}
                        onCancel={() => runTx('cancelScheduledFeeRecipient')}
                    />

                    <div className="space-y-3">
                        {FEE_PAYMENT_TYPES.map((fee) => (
                            <div
                                key={fee.key}
                                className="flex flex-col gap-2 sm:flex-row sm:items-end"
                            >
                                <div className="flex-1">
                                    <FieldLabel>{fee.label}</FieldLabel>
                                    <TextInput
                                        type="number"
                                        min="0"
                                        max={MAX_FEE_BPS / 100}
                                        step="0.01"
                                        value={feePercents[fee.key]}
                                        onChange={(e) =>
                                            setFeePercents((prev) => ({
                                                ...prev,
                                                [fee.key]: e.target.value,
                                            }))
                                        }
                                    />
                                    <p className="mt-1 text-xs text-zinc-500">
                                        On-chain:{' '}
                                        {bpsToPercentLabel(
                                            settings?.fees?.[fee.key],
                                        )}
                                    </p>
                                </div>
                                <ActionButton
                                    variant="secondary"
                                    disabled={txBusy}
                                    onClick={() =>
                                        handleSaveSingleFee(fee.value, fee.key)
                                    }
                                >
                                    Update
                                </ActionButton>
                            </div>
                        ))}
                    </div>

                    <div className="flex flex-wrap gap-2 pt-1">
                        <ActionButton disabled={txBusy} onClick={handleSaveAllFees}>
                            Save all fees
                        </ActionButton>
                        <ActionButton
                            variant="secondary"
                            disabled={txBusy}
                            onClick={handleScheduleFeeRecipient}
                        >
                            Schedule recipient change
                        </ActionButton>
                    </div>
                </SectionCard>

                <SectionCard
                    title="Dispute policy & safety"
                    description="Resolution windows and emergency pause"
                >
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                            <FieldLabel>Dispute window (hours)</FieldLabel>
                            <TextInput
                                type="number"
                                min="0"
                                step="0.1"
                                value={disputePeriodHours}
                                onChange={(e) =>
                                    setDisputePeriodHours(e.target.value)
                                }
                            />
                            <p className="mt-1 text-xs text-zinc-500">
                                Current:{' '}
                                {secondsToHoursLabel(
                                    settings?.disputeResolutionPeriodSeconds,
                                )}
                            </p>
                        </div>
                        <div>
                            <FieldLabel>Minimum period (hours)</FieldLabel>
                            <TextInput
                                type="number"
                                min="0"
                                step="0.1"
                                value={minDisputePeriodHours}
                                onChange={(e) =>
                                    setMinDisputePeriodHours(e.target.value)
                                }
                            />
                            <p className="mt-1 text-xs text-zinc-500">
                                Current:{' '}
                                {secondsToHoursLabel(
                                    settings?.minDisputeResolutionPeriodSeconds,
                                )}
                            </p>
                        </div>
                    </div>
                    <ActionButton
                        variant="secondary"
                        disabled={txBusy}
                        onClick={handleSaveDisputePeriods}
                    >
                        Update dispute periods
                    </ActionButton>

                    <div>
                        <FieldLabel>Max payment amount (raw units)</FieldLabel>
                        <TextInput
                            value={maxPaymentAmount}
                            onChange={(e) => setMaxPaymentAmount(e.target.value)}
                        />
                    </div>
                    <ActionButton
                        variant="secondary"
                        disabled={txBusy}
                        onClick={handleSaveMaxPayment}
                    >
                        Update max payment
                    </ActionButton>

                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900/50">
                        <div>
                            <p className="text-sm font-medium text-zinc-800 dark:text-zinc-200">
                                Contract pause
                            </p>
                            <p className="text-xs text-zinc-500">
                                {settings?.paused
                                    ? 'Payments are currently paused'
                                    : 'Payments are active'}
                            </p>
                        </div>
                        <ActionButton
                            variant={settings?.paused ? 'primary' : 'danger'}
                            disabled={txBusy}
                            onClick={() =>
                                runTx(settings?.paused ? 'unpause' : 'pause')
                            }
                        >
                            {settings?.paused ? 'Unpause' : 'Pause'}
                        </ActionButton>
                    </div>
                </SectionCard>

                <SectionCard
                    title="Roles & timelock"
                    description="Dispute moderator address and admin delay"
                >
                    <div>
                        <FieldLabel>Dispute admin</FieldLabel>
                        <TextInput
                            value={newDisputeAdmin}
                            onChange={(e) => setNewDisputeAdmin(e.target.value)}
                            placeholder="0x…"
                        />
                        <p className="mt-1 text-xs text-zinc-500">
                            Active:{' '}
                            {settings?.disputeAdmin
                                ? shortenWalletAddress(settings.disputeAdmin)
                                : 'none'}
                        </p>
                    </div>

                    <TimelockBanner
                        label="dispute admin"
                        pendingAddress={settings?.pendingDisputeAdmin}
                        effectiveAt={settings?.disputeAdminEffectiveAt}
                        busy={txBusy}
                        onExecute={() => runTx('executeSetDisputeAdmin')}
                        onCancel={() => runTx('cancelScheduledDisputeAdmin')}
                    />

                    <ActionButton
                        variant="secondary"
                        disabled={txBusy}
                        onClick={handleScheduleDisputeAdmin}
                    >
                        Schedule dispute admin change
                    </ActionButton>

                    <div>
                        <FieldLabel>Admin timelock delay (hours)</FieldLabel>
                        <TextInput
                            type="number"
                            min="0"
                            step="0.1"
                            value={timelockDelayHours}
                            onChange={(e) =>
                                setTimelockDelayHours(e.target.value)
                            }
                        />
                        <p className="mt-1 text-xs text-zinc-500">
                            Current:{' '}
                            {secondsToHoursLabel(
                                settings?.adminTimelockDelaySeconds,
                            )}
                        </p>
                    </div>
                    <ActionButton
                        variant="secondary"
                        disabled={txBusy}
                        onClick={handleSaveTimelockDelay}
                    >
                        Update timelock delay
                    </ActionButton>
                </SectionCard>

                <SectionCard
                    title="Token whitelist"
                    description="ERC-20 tokens accepted for payments"
                >
                    <ul className="divide-y divide-zinc-100 rounded-xl border border-zinc-200 dark:divide-zinc-800 dark:border-zinc-800">
                        {(settings?.whitelistedTokens || []).length === 0 ? (
                            <li className="px-4 py-3 text-sm text-zinc-500">
                                No whitelisted tokens loaded
                            </li>
                        ) : (
                            settings.whitelistedTokens.map((token) => (
                                <li
                                    key={token.address}
                                    className="flex items-center justify-between gap-3 px-4 py-3 text-sm"
                                >
                                    <div className="flex items-center gap-2">
                                        {token.logoURI ? (
                                            <img
                                                src={token.logoURI}
                                                alt=""
                                                className="h-6 w-6 rounded-full"
                                            />
                                        ) : null}
                                        <span className="font-medium text-zinc-800 dark:text-zinc-200">
                                            {token.symbol || 'Token'}
                                        </span>
                                        <span className="font-mono text-xs text-zinc-500">
                                            {shortenWalletAddress(token.address)}
                                        </span>
                                    </div>
                                </li>
                            ))
                        )}
                    </ul>

                    <div>
                        <FieldLabel>Add token address</FieldLabel>
                        <TextInput
                            value={newTokenAddress}
                            onChange={(e) => setNewTokenAddress(e.target.value)}
                            placeholder="0x…"
                        />
                    </div>
                    <ActionButton
                        disabled={txBusy}
                        onClick={handleWhitelistToken}
                    >
                        Whitelist token
                    </ActionButton>

                    <div>
                        <FieldLabel>Remove token</FieldLabel>
                        <select
                            className="cpg-input w-full"
                            value={removeTokenAddress}
                            onChange={(e) =>
                                setRemoveTokenAddress(e.target.value)
                            }
                        >
                            <option value="">Select token</option>
                            {(settings?.whitelistedTokens || []).map((token) => (
                                <option key={token.address} value={token.address}>
                                    {token.symbol || 'Token'} ·{' '}
                                    {shortenWalletAddress(token.address)}
                                </option>
                            ))}
                        </select>
                    </div>
                    <ActionButton
                        variant="danger"
                        disabled={txBusy || !removeTokenAddress}
                        onClick={handleRemoveToken}
                    >
                        Remove from whitelist
                    </ActionButton>
                </SectionCard>
            </div>

            {txBusy && (
                <p className="text-center text-xs text-zinc-500">
                    Waiting for wallet signature or block confirmation…
                </p>
            )}
        </div>
    );
};

export default AdminContractControls;
